import React, { Component } from "react";
import _ from "underscore";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import filterHelpers from "helpers/filter-helpers";
import GTMHelper from "helpers/gtm";
import { getSelectedSku, getSkuCount, skuInfo } from "helpers/sku-helpers";
import {
  browserPropType,
  membershipStatePropType,
  ordersPropType,
  productPropType,
  reservationPropType,
  userDataPropType,
  PDPContentPropType,
} from "components/propTypes";
import UnavailableReservationForm from "components/source/reservations/unavailable-reservation-form";
import ReservationForm from "components/source/reservations/reservation-form";
import nestFormValue from "helpers/nest-form-value";
import reservationsActions from "actions/reservations-actions";
import sharedActions from "actions/shared-actions";
import deliveryCutoffActions from "actions/delivery-cutoff-actions";
import Constants from "rtr-constants";
import ActionLogger from "action-logger";

export class ReservationFormWrapper extends Component {
  static propTypes = {
    action: PropTypes.string,
    activeOrders: ordersPropType,
    analyticsLoggingContext: PropTypes.object,
    browser: browserPropType,
    cmsContent: PDPContentPropType,
    dateOptions: PropTypes.object,
    deliveryCutoffs: PropTypes.object,
    dispatch: PropTypes.func.isRequired,
    displayedModal: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    errors: PropTypes.object,
    handleWayfindCtaClick: PropTypes.func,
    hasLengths: PropTypes.bool,
    isAdmin: PropTypes.bool,
    isBuyNow: PropTypes.bool,
    isHomepageDrawer: PropTypes.bool,
    isRentable: PropTypes.bool,
    isSubmitting: PropTypes.bool,
    logOpenAvailability: PropTypes.func,
    membershipBag: PropTypes.object,
    membershipState: membershipStatePropType,
    narrow: PropTypes.bool,
    offerBackupSize: PropTypes.bool,
    onAddToBagFailure: PropTypes.func,
    pdpUrl: PropTypes.string,
    product: productPropType,
    recommendations: PropTypes.arrayOf(PropTypes.object),
    reservation: reservationPropType,
    canRenderSplitTheBusiness: PropTypes.bool,
    userData: userDataPropType,
    workingFilters: PropTypes.object,
    isSelectedSizeAvailable: PropTypes.func,
  };

  state = {
    isMounted: false,
  };

  componentDidMount() {
    this.setState({
      isMounted: true,
    });
    this.setGtmData(this.props);
  }

  componentDidUpdate(prevProps) {
    const { reservation = {} } = this.props;
    const newZipCode = reservation?.zipCode;

    if (newZipCode) {
      this.updateShippingCutoffsInformation(newZipCode);
      this.updateNearestAvailability(newZipCode);
    }

    if (!_.isEqual(prevProps.reservation, reservation)) {
      this.setGtmData(this.props);
    }
  }

  displayModal = name => {
    this.props.dispatch(sharedActions.displayModal(name));
  };

  updateReservation = patch => {
    this.props.dispatch(reservationsActions.updateReservation(patch, this.props.pdpUrl));
  };

  logSizeSelection = (name, value) => {
    const nameTypeMatch = name.match(/(primary|backup)Sku/);
    if (!nameTypeMatch) {
      return;
    }

    const actionName = "selectdd_" + nameTypeMatch[1] + "size";

    ActionLogger.logAction({
      action: actionName,
      object_type: "node",
      style_name: this.props.product.id,
      psize: skuInfo(value).size,
    });
  };

  logZipCode = (name, value) => {
    const nameTypeMatch = name.match(/zipCode/);

    // cast null/undefined and integers into strings so we can safely do regex on them
    const valueAsString = value ? value.toString() : "";
    const completeZipMatch = valueAsString.match(/(^\d{5}$)/);

    if (!nameTypeMatch || !completeZipMatch) {
      return;
    }

    ActionLogger.logAction({
      action: "enter_zipcode",
      object_type: "node",
      zip: value,
    });
  };

  logDuration = (name, value) => {
    const nameTypeMatch = name.match(/duration/);
    if (!nameTypeMatch) {
      return;
    }
    ActionLogger.logAction({
      action: "toggle_rental_duration",
      object_type: "node",
      duration: value,
    });
  };

  registerClientSideError = errorObj => {
    this.props.dispatch(reservationsActions.clientSideError(errorObj));
  };

  // We should fetch the ShippingCutoffs information if:
  // 1. The experiment is enabled
  // 2. The zip code value changed
  // 3. The zip code value is valid
  updateShippingCutoffsInformation(zipCode) {
    const previousZipCode = this.props?.reservation?.zipCode;
    const zipCodeChanged = zipCode !== previousZipCode;

    if (!zipCodeChanged) {
      return;
    }

    if (zipCode.match(Constants.validations.zipCode.zipCodeValidator)) {
      this.props.dispatch(deliveryCutoffActions.fetchDeliveryCutoffs(zipCode));
    } else {
      this.props.dispatch(deliveryCutoffActions.clearDeliveryCutoffs());
    }
  }

  updateNearestAvailability(zipCode) {
    const previousZipCode = this.props?.reservation?.zipCode;

    if (_.isEqual(zipCode, previousZipCode)) {
      return;
    }
  }

  // The new form fields have a change callback with three arguments
  // (inputValue, isValid and name), but the old fields just pass an event.
  // onChange will handle the event and then pass it to onChangeReservationInput
  // to deal with the actual reservation logic
  onChangeReservationInput = (inputValue, isValid, name) => {
    let value = inputValue;

    if (!name.match(/^reservation\[/)) {
      return;
    }

    if (name.match(/duration/)) {
      // Cast this to an integer
      value = parseInt(inputValue, 10);
    }

    if (name.match(/(primary|backup)Sku/) && this.backupIsSameSize(name, value)) {
      // user has selected the same size for primary and backup
      this.registerClientSideError({
        isValid: false,
        primarySku: Constants.clientSideErrorMessages.reservationForm.selectDistinctSizes,
        backupSku: true,
      });
    }

    this.updateReservation(nestFormValue(name, value).reservation, this.props.pdpUrl);
    this.logSizeSelection(name, value);
    this.logZipCode(name, value);
    this.logDuration(name, value);
  };

  onChange = event => {
    const { target } = event;
    const name = target.getAttribute("name");
    let { value } = target;

    if (_.contains(["checkbox", "radio"], target.getAttribute("type")) && !target.checked) {
      // TODO: Figure out the proper value based on other elements with the same
      // name attribute
      value = null;
    }

    this.onChangeReservationInput(value, null, name, target);
  };

  setGtmData(props) {
    const date = props?.reservation?.date;
    const primarySku = props?.reservation?.primarySku?.id;

    const reservationDate = date ?? "";

    const primarySize = filterHelpers.convertSkuToSize(props.product, primarySku);

    GTMHelper.pushToDataLayer({
      event: "rtrPDPItemLoaded",
      content_size: primarySize || props.product?.canonicalSizes?.[0],
      content_reservedate: reservationDate,
    });
  }

  submittingReservationCallback = () => {
    this.props.dispatch(reservationsActions.submittingReservation());
  };

  backupIsSameSize(name, value) {
    const primarySkuValue = name.match(/primarySku/) ? value : this.props?.reservation?.primarySku?.id;
    const backupSkuValue = name.match(/backupSku/) ? value : this.props?.reservation?.backupSku?.id;

    return primarySkuValue && backupSkuValue && primarySkuValue === backupSkuValue;
  }

  isAvailable() {
    return !!_.size(this.props.product.skus);
  }

  isOneSize() {
    return this.isAvailable() && this.props.product.skus.length === 1;
  }

  renderLoader() {
    return <div className="product-reservation-form__loader" />;
  }

  renderReservationForm() {
    if (this.state.isMounted) {
      const { onAddToBagFailure } = this.props;

      const selectedSku = getSelectedSku(this.props.reservation, this.props.product.skus);
      const remaining = getSkuCount(selectedSku, this.props.product);

      return (
        <div id="product-reservation-form" data-test-id="product-reservation-form">
          <ReservationForm
            action={this.props.action}
            activeOrders={this.props.activeOrders}
            analyticsLoggingContext={this.props.analyticsLoggingContext}
            cmsContent={this.props.cmsContent}
            browser={this.props.browser}
            dateOptions={this.props.dateOptions}
            deliveryCutoffs={this.props.deliveryCutoffs}
            dispatch={this.props.dispatch}
            displayModal={this.displayModal}
            displayedModal={this.props.displayedModal}
            errors={this.props.errors}
            handleWayfindCtaClick={this.props.handleWayfindCtaClick}
            hasLengths={this.props.hasLengths}
            isBuyNow={this.props.isBuyNow}
            isHomepageDrawer={this.props.isHomepageDrawer}
            isOneSize={this.isOneSize()}
            isMobileViewport={this.props.browser?.isMobileViewport}
            isRentable={this.props.isRentable}
            isSubmitting={this.props.isSubmitting}
            logOpenAvailability={this.props.logOpenAvailability}
            membershipBag={this.props.membershipBag}
            membershipState={this.props.membershipState}
            narrow={this.props.narrow}
            offerBackupSize={this.props.offerBackupSize}
            onAddToBagFailure={onAddToBagFailure}
            onChange={this.onChange}
            onChangeReservationInput={this.onChangeReservationInput}
            product={this.props.product}
            recommendations={this.props.recommendations}
            registerClientSideError={this.registerClientSideError}
            remaining={remaining}
            reservation={this.props.reservation}
            selectedSku={selectedSku}
            submittingReservationCallback={this.submittingReservationCallback}
            canRenderSplitTheBusiness={this.props.canRenderSplitTheBusiness}
            updateReservation={this.updateReservation}
            userData={this.props.userData}
            workingFilters={this.props.workingFilters}
            isSelectedSizeAvailable={this.props.isSelectedSizeAvailable}
          />
        </div>
      );
    } else {
      return this.renderLoader();
    }
  }

  render() {
    if (!this.props.product?.published && !this.props.isAdmin) {
      return <UnavailableReservationForm />;
    }

    return this.renderReservationForm();
  }
}

const mapStateToProps = state => ({
  product: state.product,
  isBuyNow: state.isBuyNow,
  pdpUrl: state.gridProductDrawer?.pdpUrl,
});

export default connect(mapStateToProps)(ReservationFormWrapper);
